import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';

import { BulkUnarchivePrompt } from './components/BulkUnarchivePrompt';
import { SingularUnarchivePrompt } from './components/SingularUnarchivePrompt';
import { DEFAULT_DISPLAY_TEXT_FOR_CONTENT_ITEMS } from './constants';
import { UnarchivableItemDisplayText, UnarchivableItem } from './types';

type UnarchiveItemsModalProps = {
  /** Click handler when the modal is closed by either the "Cancel" button or by the modal component */
  onClose: () => void;

  /** Click handler when the user clicks the "Confirm" button to unarchive item(s) */
  onConfirm: () => void;

  /** Object containing the text to be rendered in the unarchive modal */
  displayTextForContentItems?: UnarchivableItemDisplayText;

  /** Array of content items being unarchived */
  content: UnarchivableItem[];
};

export const UnarchiveItemsModal = ({
  displayTextForContentItems = DEFAULT_DISPLAY_TEXT_FOR_CONTENT_ITEMS,
  content,
  onClose,
  onConfirm,
}: UnarchiveItemsModalProps) => {
  if (!content.length) {
    return <></>;
  }

  return (
    <Modal
      data-test="unarchive-items-modal-wrapper"
      id="unarchive-items-modal"
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader data-test="unarchive-items-modal-header">
        Unarchive{' '}
        {content.length > 1
          ? displayTextForContentItems.itemTypeName.plural
          : displayTextForContentItems.itemTypeName.singular}
        ?
      </ModalHeader>
      <ModalBody data-test="unarchive-items-modal-body" id="unarchive-items-modal-body">
        {content.length > 1 ? (
          <BulkUnarchivePrompt
            content={content}
            displayTextForContentItems={displayTextForContentItems}
          />
        ) : (
          <SingularUnarchivePrompt
            content={content}
            displayTextForContentItems={displayTextForContentItems}
          />
        )}
      </ModalBody>
      <ModalFooter
        cancelLabel="Cancel"
        data-test="unarchive-items-modal-actions"
        id="unarchive-items-modal-actions"
      >
        <PrimaryButton
          onClick={onConfirm}
          data-test="unarchive-items-modal-confirm-button"
          size={BUTTON_SIZES.LARGE}
        >
          {content.length > 1
            ? `Unarchive ${content.length} ${displayTextForContentItems.itemTypeName.plural}`
            : `Unarchive ${displayTextForContentItems.itemTypeName.singular}`}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
